import React, { useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { FolderOutline, NucleoSortDown, NucleoUnorderedList } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Radio, Stack, Typography } from '@lyearn/ui';
import SearchListEmptyState from '@/modules/corporate/components/SearchListEmptyState';
import {
  FILE_SEARCH_LIST_VARIABLES,
  FILE_SEARCH_STACK_VARIABLES,
  useFileSearch,
} from '@/modules/tasks/features/actionItems/hooks/useFileSearch';
import getSidebarItems from '@/modules/tasks/views/TasksDashboard/utils/getSidebarItems';

import { SidebarListItem, SidebarStackItem } from '../../../../Sidebar/components/StacksMenu';

type SelectionProps = {
  selectedFileId: string | null;
  selectItem: (fileId: string | null) => void;
};

type StacksListProps = {
  search: string;
  setSearch: (search: string) => void;
  showRadio?: boolean;
} & SelectionProps;

function ListItem({
  list,
  isLastItem,
  selectedFileId,
  selectItem,
  showRadio,
}: {
  list: SidebarListItem;
  isLastItem: boolean;
  showRadio?: boolean;
} & SelectionProps) {
  return (
    <Stack
      alignItems="center"
      className={cn('w-full py-10 ps-[7.2rem] pe-20 hover:bg-bg-secondary', {
        '!bg-bg-hover': selectedFileId === list.id,
        'border-b border-border': !isLastItem,
      })}
      direction="row"
      gapX={12}
      role="button"
      onClick={() => selectItem(list.id)}>
      <NucleoUnorderedList className="shrink-0 h-22 w-22" fillColorToken="icon-placeholder" />
      <TypographyWithTooltip
        className="flex-1 truncate"
        color="text-text-primary"
        numberOfLines={1}
        variant="body-short-01">
        {list.label}
      </TypographyWithTooltip>
      {showRadio ? (
        <Radio checked={selectedFileId === list.id} onChange={() => selectItem(list.id)} />
      ) : null}
    </Stack>
  );
}

function StackItem({
  stack,
  isLastItem,
  selectedFileId,
  selectItem,
  showRadio,
}: {
  stack: SidebarStackItem;
  isLastItem: boolean;
  showRadio?: boolean;
} & SelectionProps) {
  const [expanded, { toggle }] = useToggle();

  return (
    <Stack className="w-full" direction="col">
      <Stack
        alignItems="center"
        className={cn('w-full px-16 py-12 cursor-pointer hover:bg-bg-secondary', {
          'border-b border-border': !isLastItem || expanded,
        })}
        direction="row"
        gapX={8}
        role="button"
        onClick={toggle}>
        <NucleoSortDown
          className={cn(
            'shrink-0 h-18 w-18 mx-auto transition duration-150',
            !expanded && '-rotate-90 transform',
            !stack.items?.length && 'hidden',
          )}
          fillColorToken="icon-placeholder"
        />
        <TypographyWithTooltip
          className="flex-1 truncate"
          color="text-text-primary"
          numberOfLines={1}
          variant="body-short-01">
          {`${stack.label} (${stack.items?.length})`}
        </TypographyWithTooltip>
      </Stack>

      {expanded && stack.items?.length ? (
        <Stack className="w-full" direction="col">
          {stack.items.map((item, index) => (
            <ListItem
              key={item.id}
              isLastItem={isLastItem && index === (stack.items?.length ?? 0) - 1}
              list={item}
              selectItem={selectItem}
              selectedFileId={selectedFileId}
              showRadio={showRadio}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

function StacksList({ search, setSearch, selectedFileId, selectItem, showRadio }: StacksListProps) {
  const { t } = useTranslation();

  const { files: stacks } = useFileSearch({
    filters: FILE_SEARCH_STACK_VARIABLES,
    pageSize: -1,
  });

  const { files: lists } = useFileSearch({
    filters: FILE_SEARCH_LIST_VARIABLES,
    pageSize: -1,
  });

  const sidebarItems = useMemo(() => getSidebarItems({ t, stacks, lists }), [t, stacks, lists]);
  const itemsToDisplay =
    search.trim().length > 0
      ? sidebarItems
          .filter(
            (item) =>
              item.label.toLowerCase().includes(search.toLowerCase()) ||
              item.items?.some((list) => list.label.toLowerCase().includes(search.toLowerCase())),
          )
          .map((item) =>
            item.label.toLowerCase().includes(search.toLowerCase())
              ? item
              : {
                  ...item,
                  items: item.items?.filter((list) =>
                    list.label.toLowerCase().includes(search.toLowerCase()),
                  ),
                },
          )
      : sidebarItems;
  const totalCount = itemsToDisplay.length;

  return (
    <Stack className="w-full" direction="col" gapY={24}>
      <Stack alignItems="center" className="w-full" direction="row" justifyContent="between">
        <Typography className="ms-8" variant="label-01">
          {t('Task:DuplicateTask.Stacks')}
        </Typography>
        <Typography className="me-8" color="text-text-placeholder" variant="label-02">
          {t('Task:List_plural', { humanReadableCount: totalCount })}
        </Typography>
      </Stack>

      {!totalCount ? (
        <SearchListEmptyState
          Icon={FolderOutline}
          className="!my-20"
          clearSearch={() => setSearch('')}
          search={search}
          title={t('Task:MoveToListDialog.EmptyState')}
        />
      ) : (
        <Stack
          className="w-full flex-1 border border-border rounded-12 overflow-clip"
          direction="col">
          {itemsToDisplay?.map((item, index) => (
            <StackItem
              key={item.id}
              isLastItem={index === itemsToDisplay.length - 1}
              selectItem={selectItem}
              selectedFileId={selectedFileId}
              showRadio={showRadio}
              stack={item}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default StacksList;
